import React, { useState, useEffect } from 'react';
import { Tag } from '../types';

interface TagManagerProps {
  selectedTags: Tag[];
  onTagsChange: (tags: Tag[]) => void;
  availableTags: Tag[];
  onCreateTag?: (tag: Omit<Tag, 'id' | 'created_at' | 'updated_at'>) => void;
}

const TagManager: React.FC<TagManagerProps> = ({ 
  selectedTags, 
  onTagsChange, 
  availableTags,
  onCreateTag 
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewTagForm, setShowNewTagForm] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3B82F6');

  const colors = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', 
    '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16',
    '#F97316', '#6366F1', '#14B8A6', '#F43F5E'
  ];

  const filteredTags = availableTags.filter(tag => 
    tag.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    !selectedTags.some(selected => selected.id === tag.id)
  );

  const handleTagSelect = (tag: Tag) => {
    onTagsChange([...selectedTags, tag]);
    setSearchTerm('');
  };

  const handleTagRemove = (tagId: number) => {
    onTagsChange(selectedTags.filter(tag => tag.id !== tagId));
  };

  const handleCreateNewTag = () => {
    if (newTagName.trim() && onCreateTag) {
      onCreateTag({
        name: newTagName.trim(),
        color: newTagColor,
        description: ''
      });
      setNewTagName('');
      setShowNewTagForm(false);
    }
  };

  return (
    <div className="relative">
      {/* Tags Selecionadas */}
      <div className="flex flex-wrap gap-2 mb-2">
        {selectedTags.map(tag => (
          <span
            key={tag.id}
            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
            style={{ 
              backgroundColor: `${tag.color}20`, 
              color: tag.color,
              border: `1px solid ${tag.color}40`
            }}
          >
            {tag.name}
            <button
              onClick={() => handleTagRemove(tag.id)}
              className="ml-1 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </span>
        ))}
      </div>

      {/* Input de Busca */}
      <div className="relative">
        <input
          type="text"
          placeholder="🏷️ Buscar ou criar tags..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setShowDropdown(true)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
        />

        {/* Dropdown de Tags */}
        {showDropdown && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
            {/* Botão para criar nova tag */}
            {searchTerm && !filteredTags.some(tag => tag.name.toLowerCase() === searchTerm.toLowerCase()) && (
              <button
                onClick={() => setShowNewTagForm(true)}
                className="w-full px-3 py-2 text-left text-blue-600 hover:bg-blue-50 border-b"
              >
                ➕ Criar tag "{searchTerm}"
              </button>
            )}

            {/* Lista de tags disponíveis */}
            {filteredTags.map(tag => (
              <button
                key={tag.id}
                onClick={() => handleTagSelect(tag)}
                className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center"
              >
                <span
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: tag.color }}
                ></span>
                {tag.name}
              </button>
            ))}

            {filteredTags.length === 0 && !searchTerm && (
              <div className="px-3 py-2 text-gray-500 text-sm">
                Nenhuma tag disponível
              </div>
            )}
          </div>
        )}
      </div>

      {/* Formulário de Nova Tag */}
      {showNewTagForm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium mb-4">Nova Tag</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da Tag
                </label>
                <input
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="Digite o nome da tag"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cor da Tag
                </label>
                <div className="flex flex-wrap gap-2">
                  {colors.map(color => (
                    <button
                      key={color}
                      onClick={() => setNewTagColor(color)}
                      className={`w-8 h-8 rounded-full border-2 ${
                        newTagColor === color ? 'border-gray-800' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowNewTagForm(false);
                    setNewTagName('');
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreateNewTag}
                  disabled={!newTagName.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  Criar Tag
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clique fora para fechar dropdown */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

export default TagManager;